import type { JSX, ReactNode } from 'react';

import { Box, useTheme } from '@mui/material';

// Shared Vapas brand/muted/outline chip (DESIGN.md §6.4 — the non-status chip
// modifiers). `brand` = soft primary bg + primary text (active assignment,
// roles); `muted` = beige bg + grey text + border (neutral / counts);
// `outline` = transparent + grey border (counts); `danger` = soft red bg + red
// text (the §2 `paused` status palette) for state that needs attention, e.g. an
// UNassigned dispatcher chip; `mine` = soft amber bg + amber text (the §2 `mine`
// status palette) for the logged-in user's OWN assignment (my-jizdy yellow
// highlight). The optional `dot` adds the §6.4 leading `currentColor` dot.
// Reused by orders (dispatcher cell) and by Phase 2b (admin role chips) — keep
// the API general.

export type BrandChipVariant = 'brand' | 'muted' | 'outline' | 'danger' | 'mine';

export interface BrandChipProps {
  variant?: BrandChipVariant;
  dot?: boolean;
  children: ReactNode;
  // Constrain width + ellipsize long content (e.g. Czech dispatcher names).
  maxWidth?: number;
  fontSize?: number;
  className?: string;
  'data-testid'?: string;
}

const DOT_SIZE_PX = 6;

export const BrandChip = ({
  variant = 'brand',
  dot = false,
  children,
  maxWidth,
  fontSize = 11.5,
  className,
  'data-testid': dataTestId,
}: BrandChipProps): JSX.Element => {
  const theme = useTheme();

  const palette =
    variant === 'brand'
      ? { bg: theme.palette.primary.light, color: theme.palette.primary.main, border: 'transparent' }
      : variant === 'danger'
        ? {
            bg: theme.statusPalette.paused.bg,
            color: theme.statusPalette.paused.color,
            border: 'transparent',
          }
        : variant === 'mine'
          ? {
              bg: theme.statusPalette.mine.bg,
              color: theme.statusPalette.mine.color,
              border: 'transparent',
            }
        : variant === 'muted'
          ? {
              bg: theme.palette.background.default,
              color: theme.palette.text.secondary,
              border: theme.palette.divider,
            }
          : { bg: 'transparent', color: theme.palette.text.secondary, border: theme.palette.divider };

  return (
    <Box
      component="span"
      className={className}
      data-testid={dataTestId}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.375,
        borderRadius: 999,
        fontSize,
        fontWeight: 500,
        lineHeight: 1.4,
        bgcolor: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        maxWidth,
        minWidth: 0,
      }}
    >
      {dot ? (
        <Box
          component="span"
          aria-hidden
          sx={{ width: DOT_SIZE_PX, height: DOT_SIZE_PX, borderRadius: 999, bgcolor: 'currentColor', flexShrink: 0 }}
        />
      ) : null}
      <Box
        component="span"
        sx={
          maxWidth
            ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }
            : undefined
        }
      >
        {children}
      </Box>
    </Box>
  );
};
